// ============================================================================
// Google Calendar Adapter — public surface + registration helper.
//
// This file is the one the rest of the module imports. At module-load time,
// `registerGoogleCalendarAdapter()` wires the implementation into BOTH
// registries:
//
//   1. `webhook-adapters` (slug-keyed) — powers the public ingress route
//      `POST /api/v1/connectors/webhooks/google-calendar/:instanceId`.
//   2. `capability-registry` (typeId-keyed) — lets any cross-module caller
//      reach Google Calendar via
//      `call(instanceId, 'calendar', 'listEvents', ...)`.
//
// Matching up the keys: the `005_google_calendar_seed.ts` migration sets
// `connectors_types.id = connectors_types.slug = 'google-calendar'`, so the
// same string addresses the row from every angle (instance.type_id, URL
// path, capability register, webhook register). This mirrors the FB / Line
// / IG adapter pattern exactly.
// ============================================================================

import { register as registerCapability } from '../../services/capability-registry'
import { registerWebhookAdapter } from '../../services/webhook-adapters'
import { buildGoogleCalendarMethods } from './capability-methods'
import { googleCalendarWebhookAdapter } from './webhook'

/** Canonical type id / slug. MUST match the `connectors_types` seed row. */
export const GOOGLE_CALENDAR_TYPE_SLUG = 'google-calendar'

export interface RegisterGoogleCalendarOpts {
  /** Injectable fetch — tests swap it out. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
}

/**
 * Register the Google Calendar adapter in both the webhook and capability
 * registries. Idempotent: re-calling just overwrites the prior registration
 * (matches the semantics of the underlying registries). Safe to call at
 * module-load + test-reset points without special-casing.
 */
export function registerGoogleCalendarAdapter(
  opts: RegisterGoogleCalendarOpts = {},
): void {
  registerWebhookAdapter(GOOGLE_CALENDAR_TYPE_SLUG, googleCalendarWebhookAdapter)
  registerCapability(
    GOOGLE_CALENDAR_TYPE_SLUG,
    'calendar',
    buildGoogleCalendarMethods(opts),
  )
}

// ============================================================================
// Barrel — re-export the pieces consumers might reach for directly.
// ============================================================================

export {
  googleCalendarWebhookAdapter,
  verifyGoogleCalendarSignature,
  parseGoogleCalendarWebhook,
  type GoogleResourceState,
  type GoogleCalendarNotification,
} from './webhook'

export {
  buildGoogleCalendarMethods,
  type GoogleCalendarMethodsDeps,
} from './capability-methods'

export {
  // Constants
  GOOGLE_AUTHORIZE_URL,
  GOOGLE_TOKEN_URL,
  GOOGLE_TOKENINFO_URL,
  GOOGLE_CALENDAR_API_BASE,
  GOOGLE_CALENDAR_DEFAULT_SCOPES,
  // Functions
  buildGoogleCalendarAuthorizeUrl,
  exchangeGoogleCode,
  refreshGoogleToken,
  validateAccessToken,
  redactGoogleSecrets,
  // Types
  type GoogleCalendarAuthorizeInput,
  type GoogleCalendarAuthorizeResult,
  type GoogleExchangeCodeInput,
  type GoogleExchangeCodeResult,
  type RefreshGoogleTokenInput,
  type GoogleTokenInfo,
} from './oauth'

export {
  listGoogleCalendars,
  listGoogleCalendarsPage,
  type ListCalendarsPageResult,
} from './calendars'

export {
  listGoogleEvents,
  getGoogleEvent,
  createGoogleEvent,
  updateGoogleEvent,
  deleteGoogleEvent,
  type ListGoogleEventsInput,
  type GetGoogleEventInput,
  type CreateGoogleEventInput,
  type UpdateGoogleEventInput,
  type DeleteGoogleEventInput,
} from './events'

export {
  subscribeCalendarChanges,
  unsubscribeCalendarChannel,
  mintChannelId,
  mintChannelToken,
  GOOGLE_WATCH_MAX_TTL_MS,
  type SubscribeCalendarChangesInput,
  type SubscribeCalendarChangesOutput,
  type UnsubscribeCalendarChannelInput,
} from './watch'

export {
  GoogleCalendarError,
  GoogleCalendarSignatureError,
  classifyGoogleError,
  parseGoogleRetryAfterMs,
  sanitizeGoogleErrorMessage,
  type GoogleCalendarErrorCode,
  type GoogleCalendarErrorDetails,
  type GoogleResourceKind,
  type GoogleCalendarRaw,
  type GoogleCalendarListResponse,
  type GoogleEventRaw,
  type GoogleEventsListResponse,
  type GoogleWatchResponse,
  type GoogleErrorEnvelope,
} from './types'
